import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { vendorLabel, integrationKey } from '@/lib/providers/integration-state';
import { IntegrationToggleError, TOGGLE_REASON_MAX } from '@/lib/providers/integration-toggle';
import type { IntegrationDescriptor } from '@/lib/providers/types';
import { useIntegrationToggle } from '@/lib/providers/use-providers';
import { InUseNote } from './provider-table';
import { formatWhen } from './format';

/**
 * #839 — the operator's on/off switch for one integration, in two pieces:
 *
 *  · {@link IntegrationSwitch} — the "in use" cell of the row: the switch
 *    itself, or a short muted fact when there is nothing to flip ("Set at
 *    deploy time").
 *  · {@link IntegrationToggleNote} — inside the row detail: WHY the switch may
 *    not move, and who switched it last, when and why.
 *
 * The switch reflects the SERVER row, never a local guess: a flip opens a
 * confirmation that asks for a reason, and the position only moves when the BFF
 * answers with the committed row. No optimistic update. When the server says
 * the switch may not move (`toggle.allowed === false`) it is disabled and says
 * so in a word under it ("Cannot be switched off"), with the full reason in the
 * detail — never a silent grey control.
 */
export function IntegrationSwitch({
  descriptor,
  label,
}: {
  descriptor: IntegrationDescriptor;
  /** How the switch and its dialog name the binding; the vendor's name by default. */
  label?: string | undefined;
}) {
  const { t } = useTranslation('network');
  const toggle = descriptor.toggle;
  const key = integrationKey(descriptor);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const mutation = useIntegrationToggle(descriptor);

  // An older BFF sends no switch state: nothing to flip, nothing to claim.
  if (!toggle) return <InUseNote>—</InUseNote>;

  if (toggle.reason === 'not_toggleable') {
    return <InUseNote testId={`integration-toggle-static-${key}`}>{t('providers.inUseCell.fixed')}</InUseNote>;
  }

  const name = label ?? vendorLabel(t, descriptor.category, descriptor.vendor);
  const on = descriptor.enabled;
  const target = !on;
  const blocked = !toggle.allowed;
  const trimmed = reason.trim();
  const reasonMissing = trimmed.length === 0;
  const errorCode =
    mutation.error instanceof IntegrationToggleError ? mutation.error.code : mutation.error ? 'unavailable' : null;

  const openDialog = (): void => {
    mutation.reset();
    setReason('');
    setTouched(false);
    setOpen(true);
  };

  const confirm = (): void => {
    setTouched(true);
    if (reasonMissing) return;
    mutation.mutate({ enabled: target, reason: trimmed }, { onSuccess: () => setOpen(false) });
  };

  return (
    <div className="inline-flex flex-col items-end" data-testid={`integration-toggle-${key}`}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={t('providers.integrations.toggle.switchLabel', { name })}
        disabled={blocked || mutation.isPending}
        onClick={openDialog}
        data-testid={`integration-toggle-switch-${key}`}
        data-state={on ? 'on' : 'off'}
        className={cn(
          'group inline-flex min-h-11 items-center gap-2 rounded-md px-1 text-xs',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <span aria-hidden className={cn('font-medium', on ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
          {on ? t('providers.integrations.toggle.on') : t('providers.integrations.toggle.off')}
        </span>
        <span
          aria-hidden
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            on ? 'bg-emerald-600' : 'bg-muted-foreground/30',
          )}
        >
          <span
            className={cn(
              'pointer-events-none block h-4 w-4 rounded-full bg-background shadow transition-transform',
              on ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
            )}
          />
        </span>
      </button>

      {blocked ? (
        <span
          className="flex items-center gap-1 text-[11px] text-muted-foreground"
          data-testid={`integration-toggle-locked-${key}`}
        >
          <Lock className="h-3 w-3 shrink-0" aria-hidden />
          {on ? t('providers.inUseCell.cannotOff') : t('providers.inUseCell.cannotOn')}
        </span>
      ) : null}

      <Dialog open={open} onOpenChange={(next) => (mutation.isPending ? undefined : setOpen(next))}>
        <DialogContent data-testid={`integration-toggle-dialog-${key}`}>
          <DialogHeader>
            <DialogTitle>
              {target
                ? t('providers.integrations.toggle.dialog.titleOn', { name })
                : t('providers.integrations.toggle.dialog.titleOff', { name })}
            </DialogTitle>
            <DialogDescription>
              {target
                ? t('providers.integrations.toggle.dialog.bodyOn')
                : t('providers.integrations.toggle.dialog.bodyOff')}
            </DialogDescription>
          </DialogHeader>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('providers.integrations.toggle.dialog.reasonLabel')}</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={TOGGLE_REASON_MAX}
              rows={3}
              placeholder={t('providers.integrations.toggle.dialog.reasonPlaceholder')}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid={`integration-toggle-reason-${key}`}
            />
          </label>
          {touched && reasonMissing ? (
            <p className="text-xs text-destructive" data-testid={`integration-toggle-reason-required-${key}`}>
              {t('providers.integrations.toggle.dialog.reasonRequired')}
            </p>
          ) : null}
          {errorCode ? (
            <p className="text-xs text-destructive" role="alert" data-testid={`integration-toggle-error-${key}`}>
              {t(`providers.integrations.toggle.error.${errorCode}`, {
                defaultValue: t('providers.integrations.toggle.error.unavailable'),
              })}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" disabled={mutation.isPending} onClick={() => setOpen(false)}>
              {t('providers.integrations.toggle.dialog.cancel')}
            </Button>
            <Button
              type="button"
              variant={target ? 'default' : 'destructive'}
              disabled={mutation.isPending}
              onClick={confirm}
              data-testid={`integration-toggle-confirm-${key}`}
            >
              {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {mutation.isPending
                ? t('providers.integrations.toggle.dialog.saving')
                : target
                  ? t('providers.integrations.toggle.dialog.confirmOn')
                  : t('providers.integrations.toggle.dialog.confirmOff')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The switch's story, in the row detail: why it is locked (the full sentence),
 * and the operator decision in force — who, when, and in their words why — so
 * the next supervisor reads the decision, not just the state.
 */
export function IntegrationToggleNote({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t, i18n } = useTranslation('network');
  const toggle = descriptor.toggle;
  if (!toggle) return null;
  const key = integrationKey(descriptor);
  const blocked = toggle.reason && (!toggle.allowed || toggle.reason === 'not_toggleable');
  const last = toggle.lastChange;
  if (!blocked && !last) return null;

  return (
    <div className="space-y-1 text-xs">
      {blocked ? (
        <p
          className={cn(
            'flex items-start gap-1',
            toggle.reason === 'not_toggleable' ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400',
          )}
          data-testid={`integration-toggle-blocked-${key}`}
        >
          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          {t(`providers.integrations.toggle.blocked.${toggle.reason}`)}
        </p>
      ) : null}
      {last ? (
        <p className="text-muted-foreground" data-testid={`integration-toggle-last-${key}`}>
          {t('providers.integrations.toggle.lastChange', {
            state: last.enabled ? t('providers.integrations.toggle.on') : t('providers.integrations.toggle.off'),
            who: last.byName ?? last.byUrn,
            when: formatWhen(last.at, i18n.language),
          })}
          {last.except && last.except.length > 0 ? (
            <span className="block" data-testid={`integration-toggle-last-except-${key}`}>
              {t('providers.integrations.toggle.lastChangeExcept', {
                capabilities: last.except
                  .map((c) => t(`providers.routing.capability.${c}`, { defaultValue: c }))
                  .join(', '),
              })}
            </span>
          ) : null}
          {last.reason ? <span className="block italic">“{last.reason}”</span> : null}
        </p>
      ) : null}
    </div>
  );
}
